import type {
  DescriptionType,
  DiscountType,
  MoneyType,
  OrderConfigurationItemTypeExtension,
  OrderDiscountType,
  ProductAssociation,
} from "@/core/api/graphql/types";

export type ShippingChoiceType = {
  carrierName: string;
  rateModeId: string;
  zone: string;
  modeText: string;
  totalFreightAmount: number;
  minimumTransitTimeInDays: string | null;
  maximumTransitTimeInDays: string | null;
  transitTimeText: string | null;
  maxWeightLimitKg: number;
};

export type CurrencySettingsType = {
  wantsFewerPackages: boolean;
  shippingAccountNumber: boolean;
  proformaInvoice: boolean;
  invoicePDF: boolean;
  availableShippingMethods: string[];
  availablePaymentMethods: string[];
  billingDataAreaId?: string;
};

export type AccessoryType = {
  lineItemId: string;
  accessoryId: string;
  accessoryName: string;
};

export type CheckoutLineItemType = {
  id: string;
  name: string;
  imageUrl: string;
  quantity: number;
  sku: string;
  itemNumberTL?: string;
  serialNumberTL?: string;
  customerReferenceTL?: string;
  customDescriptionTL?: string;
  configurationItems?: Array<OrderConfigurationItemTypeExtension>;
  placedPrice?: MoneyType;
  listPrice: MoneyType;
  listPriceAdaptedToQuantityTL?: MoneyType;
  extendedPrice?: MoneyType;
  listPriceWithTaxAdaptedToQuantityTL?: MoneyType;
  extendedPriceWithTax?: MoneyType;
  discounts?: Array<DiscountType | OrderDiscountType>;
  product?: {
    mainProductId?: string;
    isCalibrationServiceTL?: boolean;
    isConfigurable?: boolean;
    code?: string;
    imgSrc?: string;
    images?: Array<{ url: string }>;
    descriptionsWithFallbacksTL?: Array<Pick<DescriptionType, "reviewType" | "content">>;
    masterVariation?: {
      slug?: string;
      images?: Array<{ name: string | undefined; url: string }>;
      descriptionsWithFallbacksTL?: Array<Pick<DescriptionType, "reviewType" | "content">>;
    };
    associationsTL?: ProductAssociation[];
  };
  accessoryIdTL?: string;
};

export type CheckoutItemType = {
  lineItem: CheckoutLineItemType;
  status: string;
  productTitle: string;
};

export type CheckoutShipmentType = {
  id: string;
  shipmentPriceAmount: number;
  shipmentMethodCode: string;
  shipmentMethodOption?: string;
  shippingAccountNumberTL?: string;
  businessDaysToShipTL?: string;
  wantsFewerPackagesTL?: boolean;
  hasDeliveryAddress: boolean;
};

export type CheckoutPaymentMethodType = {
  code: string;
  name: string;
};

export type DataAreaIdType = "THO" | "MUC" | "FRA" | "UK0" | "BRA" | "SWE" | "PRC" | "JPN" | "IND";

export type ContentReferenceUrlType = string | { url?: string } | null | undefined;

export interface ICheckoutLegalInfo {
  entityNameTho: string;
  entityNameMuc: string;
  entityNameFra: string;
  entityNameUk0: string;
  entityNameBra: string;
  entityNameSwe: string;
  entityNameJpn: string;
  termsAndConditionsUrlUsd: ContentReferenceUrlType;
  termsAndConditionsUrlEurMuc: ContentReferenceUrlType;
  termsAndConditionsUrlEurFra: ContentReferenceUrlType;
  termsAndConditionsUrlEurSwe: ContentReferenceUrlType;
  termsAndConditionsUrlGbp: ContentReferenceUrlType;
  termsAndConditionsUrlBrl: ContentReferenceUrlType;
  termsAndConditionsUrlJpn: ContentReferenceUrlType;
  fallbackLegalPageUrl: string;
}

// Returned by createOrderFromCartWithPaymentAuthorization when it already reported the failure to the user,
// so callers must skip their normal payment-error handling.
export const ORDER_ERROR_HANDLED = "ORDER_ERROR_HANDLED" as const;
export type OrderErrorHandledType = typeof ORDER_ERROR_HANDLED;
